"use client";
import Link from "next/link";
import { ArrowRight, Code2, MessageSquare, Phone, Calendar, GitBranch } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { apiSupport as data } from "@/lib/data/support";

const accent = "var(--color-amber)";
const channelIconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = { "Developer Slack Community": MessageSquare, "GitHub Issues": GitBranch, "Dedicated Engineer": Phone, "Office Hours": Calendar };

export function ApiSupport() {
  return (
    <Section id="api-support" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-amber)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>
        <Reveal delay={100}>
          <div className="mt-10 glass rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[rgb(var(--border)/0.18)] bg-[rgb(var(--border)/0.04)]">
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Resource</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))]">Description</th>
                    <th className="text-left px-4 py-3 eyebrow-tiny text-[rgb(var(--fg-subtle))] hidden md:table-cell">Access</th>
                  </tr>
                </thead>
                <tbody>
                  {data.resources.map((r) => (
                    <tr key={r.resource} className="border-b border-[rgb(var(--border)/0.08)]">
                      <td className="px-4 py-3 align-top font-medium" style={{ color: accent }}>{r.resource}</td>
                      <td className="px-4 py-3 align-top text-[rgb(var(--fg-muted))]">{r.description}</td>
                      <td className="px-4 py-3 align-top hidden md:table-cell"><span className="inline-flex px-2 py-0.5 rounded text-[10px] font-bold" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, color: accent }}>{r.access}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.channels.map((c, i) => {
            const Icon = channelIconMap[c.channel] ?? Code2;
            return (
              <Reveal key={c.channel} delay={i * 60}>
                <div className="glass rounded-2xl p-5 h-full" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)` }}>
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-9 w-9 rounded-lg" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)` }}>
                      <Icon className="h-4 w-4" style={{ color: accent }} />
                    </span>
                    <span className="eyebrow-tiny font-bold" style={{ color: accent }}>{c.channel}</span>
                  </div>
                  <p className="mt-2 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{c.availability}</p>
                  <p className="mt-1 text-xs font-mono" style={{ color: accent }}>{c.response}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={300}>
          <div className="mt-6 glass rounded-2xl p-5" style={{ boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)` }}>
            <span className="eyebrow-tiny font-bold" style={{ color: accent }}>API Support Resources</span>
            <ul className="mt-3 grid sm:grid-cols-2 gap-1.5">
              {data.apiResources.map((r, i) => (
                <li key={i} className="text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{r}</li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
